import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    ArrowRight, Check, Sun, Moon, Zap, Shield, Smartphone, Monitor, Mail, BarChart2, HelpCircle, X, ChevronRight, Star
} from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTranslations } from '../i18n';
import { LanguageSelector } from '../components/ui/LanguageSelector';
import { SEO } from '../components/ui/SEO';

const favicon = '/favicon_clear.png';
const faviconDark = '/favicon_clear_dark.png';

export function AlternativesPage() {
    const navigate = useNavigate();
    const { preferences, setTheme } = usePreferences();
    const { t, language } = useTranslations();
    const { user } = useAuth();
    const isDark = preferences?.theme === 'dark';

    const competitors = [
        {
            name: 'Fuelio',
            description: language === 'lv' ? 'Populāra tālruņa aplikācija degvielas uzskaitei.' : 'Popular mobile app for fuel logging.',
            pros: language === 'lv' ? ['Bezmaksas pamata funkcijas', 'Daudz lietotāju'] : ['Free basic features', 'Large user base'],
            cons: language === 'lv' ? ['Nav pilnvērtīgas Web/Desktop versijas', 'Pārsātināts UI', 'Nav e-pasta integrācijas'] : ['No full Web/Desktop version', 'Cluttered UI', 'No email integration'],
            status: 'decent'
        },
        {
            name: 'Simply Auto',
            description: language === 'lv' ? 'Visaptverošs meklētājs ar daudzām funkcijām.' : 'Comprehensive tracker with many features.',
            pros: language === 'lv' ? ['Daudz iestatījumu', 'Rematdaļu uzskaite'] : ['Many settings', 'Parts tracking'],
            cons: language === 'lv' ? ['Vecmodīgs dizains', 'Sarežģīta lietošana', 'Daudz reklāmu bezmaksas versijā'] : ['Outdated design', 'Complex to use', 'Many ads in free version'],
            status: 'clunky'
        },
        {
            name: 'MyCarCosts',
            description: language === 'lv' ? 'Moderns, datu un loģikas balstīts asistents.' : 'Modern, data and logic-driven assistant.',
            pros: language === 'lv' ? ['Email-to-Log (automātiska čeku nolasīšana)', 'Repair vs Sell loģika', 'Premium dizains', 'Web + Mobile sinhronizācija'] : ['Email-to-Log (auto receipt ingestion)', 'Repair vs Sell logic', 'Premium design', 'Web + Mobile sync'],
            cons: language === 'lv' ? ['Jauns produkts', 'Vēl nav Native aplikācijas (tikai Web App)'] : ['New product', 'No Native app yet (Web App only)'],
            status: 'premium'
        }
    ];

    const faqs = [
        {
            q: language === 'lv' ? 'Kāpēc MyCarCosts ir labāka alternatīva Fuelio?' : 'Why is MyCarCosts a better alternative to Fuelio?',
            a: language === 'lv' ? 'Fuelio ir lielisks degvielas žurnāls, taču tas kļūst nepārskatāms, kad jākārto gadiem ilga servisa vēsture. MyCarCosts piedāvā ērtu darbu uz datora un unikālu e-pasta integrāciju.' : 'Fuelio is a great fuel log, but it becomes cluttered when managing years of service history. MyCarCosts offers a smooth desktop experience and unique email integration.'
        },
        {
            q: language === 'lv' ? 'Vai es varu importēt datus no citām aplikācijām?' : 'Can I import data from other apps?',
            a: language === 'lv' ? 'Jā, mēs atbalstām CSV failu importu. Ja tev ir dati no Fuelio vai Simply Auto, tu vari tos viegli pārvietot pie mums.' : 'Yes, we support CSV file imports. If you have data from Fuelio or Simply Auto, you can easily move it to us.'
        },
        {
            q: language === 'lv' ? 'Kas ir "Repair vs. Sell" loģika?' : 'What is "Repair vs. Sell" logic?',
            a: language === 'lv' ? 'Tā ir funkcija, ko neatradīsi citās aplikācijās. Mēs analizējam tavus reālos tēriņus pret auto tirgus vērtību un pasakām, vai remonts ir finansiāli izdevīgs.' : 'It is a feature you won’t find in other apps. We analyze your real spending against car market value and tell you if a repair is financially worth it.'
        }
    ];

    return (
        <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#020617] font-primary text-slate-900 dark:text-slate-100 flex flex-col">
            <SEO 
                title={language === 'lv' ? 'Labākās auto izmaksu uzskaites alternatīvas 2026' : 'Best Car Expense Tracker Alternatives for 2026'}
                description={language === 'lv' ? 'Meklējat alternatīvu Fuelio vai Simply Auto? MyCarCosts piedāvā modernu dizainu, e-pasta integrāciju un unikālu servisa analīzi.' : 'Looking for an alternative to Fuelio or Simply Auto? MyCarCosts offers modern design, email integration, and unique service analysis.'}
            />
            
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                        <img src={isDark ? faviconDark : favicon} alt="Logo" className="w-8 h-8 object-contain" />
                        <span className="text-xl font-bold tracking-tight">My Car Costs</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LanguageSelector variant="ghost" useModal={true} />
                        <button
                            onClick={() => setTheme(preferences?.theme === 'dark' ? 'light' : 'dark')}
                            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors text-slate-600 dark:text-slate-400"
                        >
                            {preferences?.theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <button
                            onClick={() => navigate(user ? '/' : '/login')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-lg shadow-blue-500/20"
                        >
                            {user ? (t.landing.nav as any).garage : t.landing.nav.getStarted}
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero */}
                <section className="pt-20 pb-16 px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-3xl md:text-5xl font-black mb-6 leading-tight">
                            {language === 'lv' ? 'Meklē labāku auto' : 'Looking for a better'} <br />
                            <span className="text-blue-600 dark:text-blue-500">
                                {language === 'lv' ? 'uzskaites rīku?' : 'way to track cars?'}
                            </span>
                        </h1>
                        <p className="text-xl text-slate-600 dark:text-slate-400 font-medium mb-10 max-w-2xl mx-auto">
                            {language === 'lv' 
                                ? 'Mēs esam analizējuši populārākās aplikācijas un izveidojuši risinājumu, kas beidzot ir ērts, skaists un vieds.' 
                                : 'We analyzed the most popular apps and built a solution that is finally convenient, beautiful, and smart.'}
                        </p>
                    </div>
                </section>

                {/* Comparison Matrix */}
                <section className="py-12 px-4 max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {competitors.map((comp) => (
                            <div 
                                key={comp.name}
                                className={`p-8 rounded-[2.5rem] border-2 transition-all hover:shadow-2xl ${
                                    comp.status === 'premium' 
                                    ? 'bg-white dark:bg-slate-900 border-blue-600/50 shadow-xl relative scale-105 z-10' 
                                    : 'bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800 shadow-sm'
                                }`}
                            >
                                {comp.status === 'premium' && (
                                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-6 py-1.5 rounded-full text-xs font-black uppercase tracking-widest shadow-lg">
                                        {language === 'lv' ? 'Ieteicamais' : 'Recommended'}
                                    </div>
                                )}
                                <h3 className="text-3xl font-black mb-4">{comp.name}</h3>
                                <p className="text-sm text-slate-500 mb-8 leading-relaxed">{comp.description}</p>
                                
                                <div className="space-y-4 mb-8">
                                    <p className="text-xs font-black uppercase tracking-widest text-green-600 dark:text-green-500 flex items-center gap-2">
                                        <Check size={14} /> {language === 'lv' ? 'Plusi' : 'Pros'}
                                    </p>
                                    <ul className="space-y-2">
                                        {comp.pros.map(p => (
                                            <li key={p} className="text-sm font-semibold flex items-start gap-2">
                                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                                                {p}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="space-y-4">
                                    <p className="text-xs font-black uppercase tracking-widest text-red-600 dark:text-red-500 flex items-center gap-2">
                                        <X size={14} /> {language === 'lv' ? 'Mīnusi' : 'Cons'}
                                    </p>
                                    <ul className="space-y-2">
                                        {comp.cons.map(c => (
                                            <li key={c} className="text-sm font-medium text-slate-500 flex items-start gap-2">
                                                <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 flex-shrink-0" />
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Why Switch */}
                <section className="py-24 bg-white dark:bg-slate-900 mt-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-black mb-4">{language === 'lv' ? 'Kāpēc pārslēgties uz MyCarCosts?' : 'Why switch to MyCarCosts?'}</h2>
                            <p className="text-slate-500 font-medium">{language === 'lv' ? 'Mēs piedāvājam rīkus, kurus citi piemirsuši.' : 'We provide tools others have forgotten.'}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                            <div className="space-y-4">
                                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner">
                                    <Mail size={28} />
                                </div>
                                <h4 className="text-xl font-black">{language === 'lv' ? 'Email-to-Log Engine' : 'Email-to-Log Engine'}</h4>
                                <p className="text-slate-500 leading-relaxed">
                                    {language === 'lv' 
                                        ? 'Tavai mašīnai ir savs e-pasts. Aizsūti čeku, un mēs to automātiski pārvērtīsim ierakstā. Nevienai citai aplikācijai tā nav.' 
                                        : 'Your car gets its own email. Send a receipt, and we automatically turn it into an entry. No other app has this.'}
                                </p>
                            </div>
                            <div className="space-y-4">
                                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-2xl flex items-center justify-center shadow-inner">
                                    <Zap size={28} />
                                </div>
                                <h4 className="text-2xl font-black">{language === 'lv' ? 'Repair Analysis' : 'Repair Analysis'}</h4>
                                <p className="text-slate-500 leading-relaxed">
                                    {language === 'lv' 
                                        ? 'Mēs ne tikai rēķinām summas, bet pasakām, vai remonts ir loģisks, salīdzinot to ar auto tirgus vērtību un gadu pieredzi.' 
                                        : 'We don’t just sum up totals; we tell you if a repair is logical by comparing it to market value and years of data.'}
                                </p>
                            </div>
                            <div className="space-y-4">
                                <div className="w-14 h-14 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-2xl flex items-center justify-center shadow-inner">
                                    <Monitor size={28} />
                                </div>
                                <h4 className="text-2xl font-black">{language === 'lv' ? 'Web + Mobile Sync' : 'Web + Mobile Sync'}</h4>
                                <p className="text-slate-500 leading-relaxed">
                                    {language === 'lv' 
                                        ? 'Analizē savu "Total Burn" uz liela ekrāna datorā, bet datus ievadi ātri no telefona. Bez reklāmām vai traucēkļiem.' 
                                        : 'Analyze your "Total Burn" on a large screen at home, but log data quickly from your phone. No ads, no noise.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ sections for SEO */}
                <section className="py-24 px-4 bg-slate-50 dark:bg-slate-950/50">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl font-black mb-10 text-center">{language === 'lv' ? 'Biežāk uzdotie jautājumi' : 'Frequently Asked Questions'}</h2>
                        <div className="space-y-6">
                            {faqs.map(f => (
                                <div key={f.q} className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm">
                                    <h5 className="text-xl font-black mb-4">{f.q}</h5>
                                    <p className="text-slate-600 dark:text-slate-400 font-medium leading-relaxed">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="py-24 px-4 text-center">
                    <h2 className="text-4xl md:text-5xl font-black mb-8 tracking-tight">
                        {language === 'lv' ? 'Gatavs izmēģināt' : 'Ready to try'} <br />
                        <span className="text-blue-600 dark:text-blue-500">{language === 'lv' ? 'ko labāku?' : 'something better?'}</span>
                    </h2>
                    <button
                        onClick={() => navigate(user ? '/' : '/login')}
                        className="px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-[1.5rem] font-black text-xl transition-all shadow-2xl shadow-blue-500/30 hover:scale-105 active:scale-95 flex items-center gap-4 mx-auto"
                    >
                        {user ? (t.landing.nav as any).garage : t.landing.hero.startTracking}
                        <ArrowRight size={28} />
                    </button>
                    <p className="mt-8 text-slate-500 font-bold uppercase tracking-widest text-sm">
                        {language === 'lv' ? 'Nav kredītkartes. Nav stresa. Bezmaksas pirmajam auto.' : 'No credit card. No stress. Free for your first car.'}
                    </p>
                </section>
            </main>

            <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 py-12">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-slate-500 text-sm font-medium mb-4">
                        &copy; {new Date().getFullYear()} My Car Costs. {language === 'lv' ? 'Visas tiesības aizsargātas.' : 'All rights reserved.'}
                    </p>
                    <div className="flex justify-center gap-6 text-xs font-bold text-slate-400 uppercase tracking-widest">
                        <Link to="/" className="hover:text-blue-600 transition-colors">{language === 'lv' ? 'Sākums' : 'Home'}</Link>
                        <Link to="/terms" className="hover:text-blue-600 transition-colors">{t.landing.footer.terms}</Link>
                        <Link to="/privacy" className="hover:text-blue-600 transition-colors">{t.landing.footer.privacy}</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default AlternativesPage;
